import _ from 'lodash'

export type CommerceEvent =
  SuccessCommerceEvent |
  CommerceErrorEvent
export type CommerceErrorEvent =
  CarrierErrorEvent |
  PayPayErrorEvent |
  CvsNumberErrorEvent |
  CommerceErrorWithReorderLimitEvent
export type CommerceErrorWithReorderLimitEvent =
  NpPostpayErrorEvent

/** 成功時のイベント名は、未使用なこともあり詳細未定義 */
type SuccessCommerceEvent = BaseCommerceEvent<unknown, {}>

/**
 * @see https://github.com/my-color/front/issues/7122#issuecomment-1412555003
 */
type CarrierErrorCode =
  'CR1001' /* 決済放置系 */
type CarrierErrorEvent = BaseCommerceErrorEvent<
  {
    error_code: CarrierErrorCode
    note: 'キャリア決済エラー'
  },
  {
    /**
     * @see https://my-color.esa.io/posts/591 値の意味は"クロネコドキュメント 2.9.1"を参照
     * @see https://github.com/my-color/front/issues/7122#issuecomment-1412516879
     */
    settle_detail: '11' | '12' | '13' | '14' | '15'
    settle_price: string
  }
> |
BaseCommerceErrorEvent<
  {
    error_code: PayPayErrorCode
    note: 'キャリア決済放置注文'
  }
>

/**
 * キャリア決済のエラーコードを流用してしまっている可能性
 * @see https://github.com/my-color/front/issues/7122#issuecomment-1412555003
 */
type PayPayErrorCode =
  'CR1001' /* 決済放置系 */
type PayPayErrorEvent = BaseCommerceErrorEvent<
  {
    error_code: PayPayErrorCode
    note: 'PayPay決済放置注文'
  }
>

/**
 * コンビニ決済エラー
 */
type CvsNumberErrorEvent = BaseCommerceErrorEvent<
  {
    /**
     * @see https://github.com/my-color/front/issues/7122#issuecomment-1412516879
     */
    error_code: CvsNumberErrorEvent['content']['settle_detail']
  },
  {
    /**
     * | 値 | 意味 |
     * | --- | --- |
     * | 11 | 購入者都合エラー（支払期限切れ、コンビニエンスストアから入金取消の通知が発生した場合等） |
     * | 13 | 決済機関都合エラー（コンビニエンスストアより応答が無い場合、異常の応答を受けた場合等） |
     * | 14 | その他システムエラー |
     *
     * @see https://github.com/my-color/front/issues/7122#issuecomment-1412516879
     */
    settle_detail: '11' | '13' | '14'
  }
>

type NpPostpayErrorEvent = BaseCommerceErrorWithReorderLimitEvent<
  {
    /**
     * ER{number} or RE{number}
     *
     * | コード形式 | 意味 |
     * | --- | --- |
     * | ER始まり | 取引登録エラー |
     * | NG999 | 与信NG |
     * | RE始まり | 与信保留（審査継続無し） |
     *
     * @see https://everforth.backlog.jp/view/PJ_MYCOLOR-1934
     * @note TypeScript4.1以降にバージョンアップしたら、TemplateLiteralTypesで「ER始まり」「RE始まり」のパターンを定義したい
     * @note {@link https://www.typescriptlang.org/docs/handbook/2/template-literal-types.html}
     */
    error_code: string
  }
>

export function errorEventWithReorderLimitDate(
  event: CommerceEvent
): event is CommerceErrorWithReorderLimitEvent {
  return _.get(event, 'content.reorder_limit_date')
}

interface BaseCommerceEvent<T, C extends object> {
  name: T
  content: C
}

// エラー系
const errorCommands = [
  'payment-settlement-error-commerce',
  'payment-settlement-fatal-error-commerce',
] as const
type ErrorCommand = typeof errorCommands[number]

export function isErrorEvent(event: CommerceEvent): event is CommerceErrorEvent {
  return errorCommands.some(type => event.name === type)
}

interface ErrorDetail {
  readonly error_code: string
  readonly note?: string
}
interface ContentWithErrorDetail<E extends ErrorDetail> {
  /**
   * Engineのcommerce_event[].content.errorsは要素が1つの場合は配列でなくオブジェクトで登録されていた過去がある
   *
   * @see https://everforth.backlog.jp/view/PJ_MYCOLOR-2085
   */
  readonly errors: E[] | E
}
type BaseCommerceErrorEvent<
  E extends ErrorDetail,
  C extends object = {}
> = BaseCommerceEvent<
  ErrorCommand,
  C & ContentWithErrorDetail<E>
>
type BaseCommerceErrorWithReorderLimitEvent<
  E extends ErrorDetail,
  C extends object = {}
> = BaseCommerceEvent<
  ErrorCommand,
  C & ContentWithErrorDetail<E> & {
    readonly reorder_limit_date: string
  }
>
